import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { consultarVerificacion, type Verificacion } from "@/lib/inscripciones";

type Resultado =
  | { tipo: "encontrado"; datos: Verificacion }
  | { tipo: "no-encontrado" }
  | { tipo: "error" }
  | null;

const INFO_ESTADO = {
  aprobado: {
    titulo: "¡Inscripción APROBADA!",
    detalle: "Tu cupo está confirmado. ¡Nos vemos en la partida!",
    color: "bg-green-500/15 text-green-300 ring-green-400/40",
    icono: "ri-checkbox-circle-line",
  },
  pendiente: {
    titulo: "Inscripción EN REVISIÓN",
    detalle: "Recibimos tu inscripción y estamos validando tu pago. Vuelve a consultar pronto.",
    color: "bg-yellow-500/15 text-yellow-300 ring-yellow-400/40",
    icono: "ri-time-line",
  },
  negado: {
    titulo: "Inscripción NO APROBADA",
    detalle: "Tu inscripción no pudo ser aprobada. Contáctanos para más información.",
    color: "bg-red-500/15 text-red-300 ring-red-400/40",
    icono: "ri-close-circle-line",
  },
} as const;

export default function VerificarPage() {
  const [cedula, setCedula] = useState("");
  const [cargando, setCargando] = useState(false);
  const [resultado, setResultado] = useState<Resultado>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!cedula.trim()) return;
    setCargando(true);
    setResultado(null);
    try {
      const datos = await consultarVerificacion(cedula);
      setResultado(datos ? { tipo: "encontrado", datos } : { tipo: "no-encontrado" });
    } catch (err) {
      console.error(err);
      setResultado({ tipo: "error" });
    } finally {
      setCargando(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-950 via-secondary-900 to-primary-900 text-white">
      {/* Encabezado */}
      <header className="sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-heading text-xl tracking-wide">
            <i className="ri-arrow-left-line" /> BAÑOS RUN 2026
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Texto */}
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 ring-1 ring-white/20 text-xs font-semibold tracking-widest uppercase">
              <span className="w-2 h-2 rounded-full bg-green-400" /> Sistema de verificación 2026
            </span>
            <h1 className="font-heading text-5xl md:text-6xl tracking-wide mt-5 leading-none">
              VERIFICA TU
              <br />
              <span className="text-primary-400">INSCRIPCIÓN</span>
            </h1>
            <p className="text-white/70 mt-5 text-base md:text-lg max-w-md">
              Ingresa tu número de cédula para consultar si tu inscripción ya fue aprobada.
            </p>
          </div>

          {/* Tarjeta de consulta */}
          <div className="bg-white/5 backdrop-blur-sm ring-1 ring-white/15 rounded-3xl p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <i className="ri-id-card-line text-2xl text-primary-300" />
              </div>
              <div>
                <p className="font-semibold text-lg">Consultar cédula</p>
                <p className="text-sm text-white/60">Ingresa tus datos para validar</p>
              </div>
            </div>

            <form onSubmit={onSubmit}>
              <label className="block text-xs font-semibold tracking-widest uppercase text-white/60 mb-2">
                Número de identificación
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={cedula}
                onChange={(e) => setCedula(e.target.value)}
                placeholder="Ej: 1801234567"
                className="w-full px-4 py-3.5 rounded-xl bg-white/10 ring-1 ring-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />

              <button
                type="submit"
                disabled={cargando}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-700 text-white font-semibold hover:from-primary-400 hover:to-primary-600 transition-colors disabled:opacity-60"
              >
                <i className="ri-ticket-2-line" />
                {cargando ? "CONSULTANDO…" : "CONSULTAR INSCRIPCIÓN"}
              </button>
            </form>

            {/* Resultado */}
            {resultado?.tipo === "encontrado" && (() => {
              const info = INFO_ESTADO[resultado.datos.estado];
              return (
                <div className={`mt-6 rounded-2xl p-5 ring-1 ${info.color}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <i className={`${info.icono} text-2xl`} />
                    <p className="font-bold text-lg">{info.titulo}</p>
                  </div>
                  <p className="text-sm text-white/80">{info.detalle}</p>
                  <div className="mt-4 pt-4 border-t border-white/15 text-sm space-y-1 text-white/90">
                    <p><span className="text-white/60">Atleta:</span> {resultado.datos.nombres}</p>
                    <p><span className="text-white/60">Cédula:</span> {resultado.datos.cedula}</p>
                    <p><span className="text-white/60">Distancia:</span> {resultado.datos.distancia}</p>
                    <p><span className="text-white/60">Categoría:</span> {resultado.datos.categoria}</p>
                  </div>
                </div>
              );
            })()}

            {resultado?.tipo === "no-encontrado" && (
              <div className="mt-6 rounded-2xl p-5 ring-1 bg-white/5 ring-white/15">
                <p className="font-semibold">No encontramos una inscripción con esa cédula.</p>
                <p className="text-sm text-white/70 mt-1">
                  Verifica el número, o si aún no te has inscrito,{" "}
                  <Link to="/inscribirse" className="text-primary-300 underline">hazlo aquí</Link>.
                </p>
              </div>
            )}

            {resultado?.tipo === "error" && (
              <div className="mt-6 rounded-2xl p-5 ring-1 bg-red-500/15 ring-red-400/40 text-red-200">
                Ocurrió un error al consultar. Intenta de nuevo.
              </div>
            )}

            <p className="text-center text-sm text-white/50 mt-6">
              ¿Tienes problemas?{" "}
              <a href="https://wa.me/593979102532" target="_blank" rel="noopener noreferrer" className="text-white underline font-semibold">
                Contáctanos
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
